import { CodeOutlined, DesktopOutlined } from "@ant-design/icons";
import { Instructions as InstructionsType } from "@p0security/shared/install/types";
import { Button, Spin, Tabs, Typography } from "antd";
import { VerticalSpacedDiv } from "components/divs";
import { compact } from "lodash";
import { ReactNode, useMemo } from "react";

import { CommandDisplay } from "../CommandDisplay";
import { Logo } from "../Logo";

type InstructionsProps = {
  instructions: InstructionsType<ReactNode>;
  isFetching: boolean;
  onNext: () => void;
};

export const TerraformLogo = (
  // Downloaded from https://brand.hashicorp.com/product_logos
  // This usage is allowed under https://www.hashicorp.com/en/trademark-policy:
  // "When using HashiCorp marks, use an appropriate attribution and proprietary rights notice, such as:
  // [Mark] and the [Logo name] are trademarks of HashiCorp."
  //
  // In addition, the section on logos says:
  // "We currently allow (without separate permission required) the use of the current versions of the
  // graphic logos for our source-available projects on your own website, so long as they are used only
  // to identify and hyperlink to main page of the specific HashiCorp project website (or to www.hashicorp.com)"
  //
  // We satisfy these by:
  // - Adding proprietary rights notice in a popover ("Terraform is trademark of HashiCorp.")
  // - Adding hyperlink that links to Terraform website in the same popover
  <Logo
    logo={"/Terraform-LogoMark_onLight.svg"}
    title={"Terraform"}
    size={16}
    trademark={{
      notice: "Terraform is a trademark of HashiCorp.",
      url: "https://www.terraform.io",
    }}
  />
);

export const Instructions = ({
  instructions,
  isFetching,
  onNext,
}: InstructionsProps) => {
  const { commands, help } = instructions;

  const tabOptions = useMemo(
    () =>
      compact([
        commands?.console && {
          label: (
            <>
              <DesktopOutlined />
              &nbsp;Console
            </>
          ),
          key: "console",
          children: commands.console,
        },
        commands?.shell && {
          label: (
            <>
              <CodeOutlined />
              &nbsp;Shell
            </>
          ),
          key: "shell",
          children: commands.shell.map(({ header, command }, ix) => (
            <div key={ix}>
              {header ?? (
                <Typography.Paragraph>
                  Run these shell commands, then click &quot;Next&quot;:
                </Typography.Paragraph>
              )}
              <CommandDisplay commands={command} maxRows={20} />
            </div>
          )),
        },
        commands?.iac && {
          label: <>{TerraformLogo}&nbsp;Terraform</>,
          key: "iac",
          children: commands.iac.map(({ header, command }, ix) => (
            <div key={ix}>
              {header ?? (
                <Typography.Paragraph>
                  Add the following to your Terraform config, then apply, then
                  click &quot;Next&quot;:
                </Typography.Paragraph>
              )}
              <CommandDisplay commands={command} maxRows={20} />
            </div>
          )),
        },
      ]),
    [commands]
  );

  return isFetching ? (
    <Spin />
  ) : (
    <VerticalSpacedDiv style={{ marginBottom: "14px" }}>
      <div>
        {(help as ReactNode) ?? "To continue installation, run the following:"}
      </div>
      {tabOptions?.length ? <Tabs items={tabOptions} /> : null}
      <div>
        <Button type="primary" onClick={onNext}>
          Next
        </Button>
      </div>
    </VerticalSpacedDiv>
  );
};
